import React, { useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, useSession } from '@cordata/buffet';

import {
  authorizedPyInstance as py,
} from '~/utils';
import { differenceInMilliseconds, format } from 'date-fns';

const SessionTimeoutContext = createContext({});

const minutesToMilliseconds = m => m * 60 * 1000;

const TIMEOUT_SOFT_LIMIT = minutesToMilliseconds(50); // time before warning modal is shown
const TIMEOUT_HARD_LIMIT = minutesToMilliseconds(60); // time before user is kicked out

const SessionTimeoutProvider = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const { isAuthed } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(format(new Date(TIMEOUT_HARD_LIMIT - TIMEOUT_SOFT_LIMIT), 'mm:ss'));

  const toggleModal = () =>
    setIsOpen(p => !p);

  const refreshToken = () => {
    py.get('current').then(() => {
      localStorage.setItem('lastActivity', new Date());

      setIsOpen(false);
    });
  };

  const checkRemainingTime = () => {
    const lastActivity = localStorage.getItem('lastActivity');
    const timeSinceLastActivity = differenceInMilliseconds(new Date(), lastActivity);

    if (location.pathname === '/session-timeout') {
      setIsOpen(false);
    } else if (
      isAuthed &&
      lastActivity &&
      timeSinceLastActivity >= TIMEOUT_HARD_LIMIT &&
      location.pathname !== '/session-timeout'
    ) {
      // kick user
      setIsOpen(false);
      history.push('/session-timeout');
    } else if (
      isAuthed &&
      lastActivity &&
      timeSinceLastActivity >= TIMEOUT_SOFT_LIMIT
    ) {
      // show warning modal with countdown timer
      const millisecondsRemaining = TIMEOUT_HARD_LIMIT - timeSinceLastActivity;
      const formatted = format(new Date(millisecondsRemaining), 'mm:ss');
      setTimeRemaining(formatted);
      setIsOpen(true);
    } else {
      // user is good for a while
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isAuthed && location.pathname !== '/session-timeout') {
      localStorage.setItem('lastActivity', new Date());
    }

    const timeoutInterval = setInterval(() => {
      checkRemainingTime();
    }, 1000);

    return () => clearInterval(timeoutInterval);
  }, [isAuthed, location]);

  return (
    <SessionTimeoutContext.Provider value={{ setIsOpen }}>
      {children}

      <Modal isOpen={isOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Session Inactivity</ModalHeader>

        <ModalBody>
          <p>
            Your session will expire in {timeRemaining}.{' '}
            Inactivity will potentially result in loss of work.
          </p>

          <p>Are you still active?</p>
        </ModalBody>

        <ModalFooter>
          <Button color="primary" icon="check" onClick={refreshToken}>
            Yes, I am still active
          </Button>
        </ModalFooter>
      </Modal>
    </SessionTimeoutContext.Provider>
  );
};

SessionTimeoutProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SessionTimeoutProvider;
export { SessionTimeoutContext };

